/**
 * Solo dummy-practice setup screen (#609). Deliberately a SEPARATE,
 * dedicated screen from the (tester-gated) `PracticeSetupScreen` — Option B
 * from the issue's resolved discussion: reusing the gated screen risked the
 * ungated dummy option accidentally inheriting the tester-flag check on a
 * future edit. This screen's own `createMatch` request is NEVER gated
 * server-side (`ws.ts`'s explicit `{ kind: "dummy" }` carve-out from
 * `isTesterFlagged()`) — every user reaches it, no client-side gating at
 * all, matching the server's own genuinely-open behavior.
 *
 * 1v1 only: pick your own champion/loadout (the same `ChampionPicker`/
 * `LoadoutPicker` every other setup screen uses), then create — the OTHER
 * seat is always `{ kind: "dummy" }`, a genuinely no-op controller
 * (`botSeat.ts`'s `addDummySeat`) — no opponent picker, nothing to choose.
 *
 * Saveable/replayable: a successful create saves champion/loadout only
 * (`prefs.ts`'s `saveDummyPracticeConfig`) — there is no seat plan worth
 * persisting (the opponent is always the same no-op dummy).
 */

import { useEffect, useState, type ReactElement } from "react";
import type { AbilityId } from "@pit/protocol";
import { useWsClient } from "../net/WsClientProvider.js";
import { useStore } from "../store/context.js";
import { navigate } from "../router/hashRoute.js";
import { ChampionPicker } from "../champions/ChampionPicker.js";
import { LoadoutPicker, isValidLoadoutSize } from "../champions/LoadoutPicker.js";
import { defaultLoadout } from "../champions/loadout.js";
import { loadDummyPracticeConfig, saveDummyPracticeConfig } from "../prefs.js";
import { resolveSavedChampionId, resolveSavedLoadout } from "./resolvePracticeConfig.js";

export function DummyPracticeScreen(): ReactElement {
  const client = useWsClient();
  const { state, dispatch } = useStore();
  const [pending, setPending] = useState(false);

  const saved = loadDummyPracticeConfig();
  const [championId, setChampionId] = useState(() => resolveSavedChampionId(saved));
  const [loadout, setLoadout] = useState<readonly AbilityId[]>(() =>
    resolveSavedLoadout(saved, resolveSavedChampionId(saved)),
  );

  const loadoutValid = isValidLoadoutSize(championId, loadout);
  const canConnect = state.connection.kind === "welcomed";
  const canCreate = canConnect && loadoutValid;

  // Same create → hop-to-Match flow as HomeScreen's "Create private match".
  useEffect(() => {
    if (pending && state.matchId !== null && state.joinUrl !== null) {
      navigate({ kind: "match" });
      setPending(false);
    }
  }, [pending, state.matchId, state.joinUrl]);

  useEffect(() => {
    if (pending && state.serverError !== null) {
      setPending(false);
    }
  }, [pending, state.serverError]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        flexDirection: "column",
        gap: 16,
        padding: 32,
      }}
    >
      <h1 style={{ margin: 0 }}>Solo dummy practice</h1>
      <p style={{ opacity: 0.7, fontSize: 13, maxWidth: 420, textAlign: "center" }}>
        Practice alone against a stationary dummy that never moves, casts, or
        fights back. No setup needed beyond your own champion and loadout.
      </p>
      <ChampionPicker
        value={championId}
        onChange={(id) => {
          setChampionId(id);
          setLoadout(defaultLoadout(id));
        }}
        disabled={!canConnect}
      />
      <LoadoutPicker
        championId={championId}
        value={loadout}
        onChange={setLoadout}
        disabled={!canConnect}
      />
      <button
        type="button"
        data-testid="dummy-practice-create"
        disabled={!canCreate}
        onClick={() => {
          if (state.matchId !== null) dispatch({ type: "match.leave" });
          dispatch({ type: "match.champion", championId, loadout });
          client.createMatch(championId, [...loadout], {
            opponentSeats: [{ kind: "dummy" }],
          });
          saveDummyPracticeConfig({ championId, loadout: [...loadout] });
          setPending(true);
        }}
        style={{
          padding: "10px 24px",
          fontSize: 15,
          background: canCreate ? "#3aa675" : "#333",
          color: "#fff",
          border: "none",
          borderRadius: 6,
        }}
      >
        Create dummy practice match
      </button>
      <button
        type="button"
        data-testid="dummy-practice-back"
        onClick={() => {
          navigate({ kind: "home" });
        }}
        style={{
          padding: "8px 16px",
          background: "transparent",
          color: "#dfe6ef",
          border: "1px solid #3a4757",
          borderRadius: 6,
        }}
      >
        Back
      </button>
      {!canConnect ? <div style={{ opacity: 0.6 }}>connecting…</div> : null}
    </div>
  );
}
